import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { RefreshCw, MoreHorizontal, X } from "lucide-react";
import Table from "../../../components/ACS-Dashboard/ACS-Blocks/Table";
import GlobalPagination from "../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import ActionMenu from "../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";

// ─── Constants ────────────────────────────────────────────────────────────────

const TABLE_HEADERS = [
    "Selection",
    "Action",
    "Transaction No.",
    "File Type",
    "Flow Type",
    "Ref. Type",
    "Reference Number",
    "File Name",
    "Source",
    "Processed",
    "Date/Time",
];

const SORTABLE_COLUMNS = {
    "Transaction No.":  "transactionNumber",
    "File Type":        "fileType",
    "Flow Type":        "flowType",
    "Ref. Type":        "referenceType",
    "Reference Number": "referenceNumber",
    "File Name":        "fileName",
    "Processed":        "processingStatus",
    "Date/Time":        "dateTimeProcessed",
    "Source":           "source",
};

const FILE_TYPE_OPTIONS = [
    "All File Types",
    "850 New", "850 Update", "Cancel Order", "Status Update (In)",
    "990", "214", "210",
    "204 Tender", "204 Update", "204 Recall", "204 Cancel",
    "204 Stop Reorder", "204 Charge Update", 
    "211 New", "211 Update", "Status Update (Out)",
    "Invoice", "997 ACK 990", "997 ACK 214", "997 ACK 210",
];

const FILE_TYPE_MAP = {
  "All File Types": "",
  "850 New": "EDI_850_NEW",
  "850 Update": "EDI_850_UPDATE",
  "Cancel Order": "EDI_CANCEL_ORDER",
  "Status Update (In)": "EDI_STATUS_UPDATE_INBOUND",
  990: "EDI_990",
  214: "EDI_214",
  210: "EDI_210",
  "204 Tender": "EDI_204_TENDER",
  "204 Update": "EDI_204_UPDATE",
  "204 Recall": "EDI_204_RECALL",
  "204 Cancel": "EDI_204_CANCEL",
  "204 Stop Reorder": "EDI_204_STOP_REORDER",
  "204 Charge Update": "EDI_204_CHARGE_UPDATE",
  "211 New": "EDI_211_NEW",
  "211 Update": "EDI_211_UPDATE",
  "Status Update (Out)": "EDI_STATUS_UPDATE_OUTBOUND",
  "Invoice": "EDI_INVOICE",
  "997 ACK 990": "EDI_997_ACK_990",
  "997 ACK 214": "EDI_997_ACK_214",
  "997 ACK 210": "EDI_997_ACK_210",
};
const SOURCE_MAP = {
    "All Sources": "",
    "IFS":         "IFS",
    "Carrier EDI": "CARRIER_EDI",
    "Ascend":      "ASCEND",
};


const FLOW_OPTIONS   = ["All Flows",     "Inbound",   "Outbound"];
const STATUS_OPTIONS = ["All Statuses",  "Successful","Unsuccessful"];
const REF_OPTIONS    = ["All Ref Types", "ORDER",     "SHIPMENT", "INVOICE"];
const SOURCE_OPTIONS = ["All Sources", "IFS", "Carrier EDI", "Ascend"];

// exact same td style as MyInvoicesTab
const TD_STYLE = {
    whiteSpace:   "nowrap",
    overflow:     "hidden",
    textOverflow: "ellipsis",
    width:        "200px",
    minWidth:     "200px",
    maxWidth:     "200px",
};
// exact same td class as MyInvoicesTab
const TD_CLASS =
    "px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text " +
    "text-xs sm:text-sm md:text-base border-b border-gray-200 " +
    "dark:border-acs-dark-border font-roboto transition-none";
// ─── Component ────────────────────────────────────────────────────────────────

const TransactionAudit = () => {
    const [data, setData]                         = useState([]);
    const [totalElements, setTotalElements]       = useState(0);
    const [totalPages, setTotalPages]             = useState(1);
    const [loading, setLoading]                   = useState(false);
    const [reloading, setReloading]               = useState(false);
    const [error, setError]                       = useState(null);

    const [searchQuery, setSearchQuery]           = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");

    const [fileType, setFileType]                 = useState("");
    const [flowType, setFlowType]                 = useState("");
    const [referenceType, setReferenceType]       = useState("");
    const [processingStatus, setProcessingStatus] = useState("");
    const [source, setSource]                     = useState("");

    const [fileTypeLabel, setFileTypeLabel]       = useState("All File Types");
    const [flowTypeLabel, setFlowTypeLabel]       = useState("All Flows");
    const [refTypeLabel, setRefTypeLabel]         = useState("All Ref Types");
    const [statusLabel, setStatusLabel]           = useState("All Statuses");
    const [sourceLabel, setSourceLabel]           = useState("All Sources");

    const [currentPage, setCurrentPage]           = useState(1);
    const [itemsPerPage, setItemsPerPage]         = useState(50);

    const [sortBy, setSortBy]                     = useState("dateTimeProcessed");
    const [ascending, setAscending]               = useState(false);

    const [openMenu, setOpenMenu]                 = useState(null);
    const [dropdownPos, setDropdownPos]           = useState({ top: 0, left: 0 });
    const dropdownRef                             = useRef(null);
    const checkboxHeaderRef                       = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedKeyword(searchQuery);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [searchQuery]);

    useEffect(() => {
        const handle = (e) => {
            if (e.target.closest("[data-action-trigger]")) return;
            if (dropdownRef.current && !dropdownRef.current.contains(e.target))
                setOpenMenu(null);
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: currentPage - 1,
                size: itemsPerPage,
                sort: `${sortBy},${ascending ? "asc" : "desc"}`,
                ...(debouncedKeyword  && { keyword: debouncedKeyword }),
                ...(fileType          && { fileType }),
                ...(flowType          && { flowType }),
                ...(referenceType     && { referenceType }),
                ...(processingStatus  && { processingStatus }),
                ...(source            && { source }),
            };
            const res = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/transaction-audit`,
                { params, headers: { Authorization: `Bearer ${Cookies.get("authToken")}` } }
            );
            setData(res.data.content || []);
            setTotalElements(res.data.totalElements || 0);
            setTotalPages(res.data.totalPages || 1);
        } catch {
            setError("Failed to load transaction audit data.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, sortBy, ascending, debouncedKeyword,
        fileType, flowType, referenceType, processingStatus, source]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSearch  = () => { setDebouncedKeyword(searchQuery); setCurrentPage(1); };
    const handleReload  = () => { setReloading(true); fetchData().finally(() => setReloading(false)); };

    const handleSort = (headerLabel) => {
        const key = SORTABLE_COLUMNS[headerLabel];
        if (!key) return;
        if (sortBy === key) setAscending((p) => !p);
        else { setSortBy(key); setAscending(true); }
        setCurrentPage(1);
    };

    const handleColFilter = (colKey, displayVal) => {
        const isAll  = displayVal.startsWith("All");
        let apiVal;
        if (colKey === "fileType") {
          apiVal = FILE_TYPE_MAP[displayVal] ?? "";
        } else if (colKey === "source") {
          apiVal = SOURCE_MAP[displayVal] ?? "";
        } else if (colKey === "flowType" || colKey === "processingStatus") {
          if (isAll) {
            apiVal = "";
          } else if (displayVal === "Successful") {
            apiVal = "SUCCESSFUL";
          } else if (displayVal === "Unsuccessful") {
            apiVal = "UNSUCCESSFUL";
          } else {
            apiVal = displayVal.toUpperCase();
          }
        } else {
          // referenceType — ORDER, SHIPMENT, INVOICE already uppercase
          apiVal = isAll ? "" : displayVal;
        }

        if (colKey === "fileType")         { setFileType(apiVal);         setFileTypeLabel(displayVal); }
        if (colKey === "flowType")         { setFlowType(apiVal);         setFlowTypeLabel(displayVal); }
        if (colKey === "referenceType")    { setReferenceType(apiVal);    setRefTypeLabel(displayVal); }
        if (colKey === "processingStatus") { setProcessingStatus(apiVal); setStatusLabel(displayVal); }
        if (colKey === "source")           { setSource(apiVal);           setSourceLabel(displayVal); }
        setCurrentPage(1);
    };

    const handleActionClick = (id, e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
        setOpenMenu((prev) => (prev === id ? null : id));
    };

    const formatFileType = (val) => {
      if (!val) return "—";
      return val
            .replace(/^EDI_/, "")
            .replace("_INBOUND", "_IN")
            .replace("_OUTBOUND", "_OUT");
    };

    const formatDateTime = (val) =>
        val ? new Date(val).toLocaleString() : "—";

    // rows for Table component
    const rows = data.map((row) => ({ ...row, _id: row.id }));

    return (
      <div className="p-fluid-1 shadow-md flex flex-col font-roboto flex-1 h-full min-h-0 overflow-hidden bg-custom-bg-purple">
        {/* top action bar  */}
        <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={handleSearch}
              placeholder="Search transaction no., reference..."
            />
            {/* File Type filter */}
            <select
              value={fileTypeLabel}
              onChange={(e) => handleColFilter("fileType", e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-white focus:outline-none focus:border-[#223688]"
            >
              {FILE_TYPE_OPTIONS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>

            {/* Flow Type filter */}
            <select
              value={flowTypeLabel}
              onChange={(e) => handleColFilter("flowType", e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-white focus:outline-none focus:border-[#223688]"
            >
              {FLOW_OPTIONS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>

            {/* Ref Type filter */}
            <select
              value={refTypeLabel}
              onChange={(e) => handleColFilter("referenceType", e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-white focus:outline-none focus:border-[#223688]"
            >
              {REF_OPTIONS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>

            <select
              value={sourceLabel}
              onChange={(e) => handleColFilter("source", e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-white focus:outline-none focus:border-[#223688]"
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>

            {/* Processed filter */}
            <select
              value={statusLabel}
              onChange={(e) =>
                handleColFilter("processingStatus", e.target.value)
              }
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-white focus:outline-none focus:border-[#223688]"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>

            {/* ← ADD CLEAR BUTTON HERE */}
            {(fileType ||
              flowType ||
              referenceType ||
              processingStatus ||
              source ||
              searchQuery) && (
              <button
                onClick={() => {
                  setFileType("");
                  setFlowType("");
                  setReferenceType("");
                  setProcessingStatus("");
                  setSearchQuery("");
                  setDebouncedKeyword("");
                  setSource("");
                  setSourceLabel("All Sources");
                  setFileTypeLabel("All File Types");
                  setFlowTypeLabel("All Flows");
                  setRefTypeLabel("All Ref Types");
                  setStatusLabel("All Statuses");
                  setCurrentPage(1);
                }}
                className="flex items-center gap-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <X size={14} />
                Clear filters
              </button>
            )}

            <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
              {totalElements} Transactions
            </span>
            <button
              onClick={handleReload}
              className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30 transition"
              title="Reload"
              aria-label="Reload"
            >
              <RefreshCw
                size={16}
                className={`text-gray-500 ${reloading ? "animate-spin" : "hover:text-gray-700"}`}
              />
            </button>
          </div>
        </div>

        {/* table wrapper  */}
        <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col bg-white dark:bg-acs-dark-surface rounded-xl overflow-hidden">
          {error ? (
            <div className="flex-1 flex items-center justify-center text-red-400 text-sm">
              {error}
            </div>
          ) : loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <div className="w-4 h-4 border-2 border-[#223688] border-t-transparent rounded-full animate-spin" />
                Loading...
              </div>
            </div>
          ) : (
            <>
              {/* filter row above table */}

              <div className="flex-1 min-h-[200px] bg-white sm:min-h-[300px] overflow-x-auto overflow-y-auto">
                <Table
                  headers={TABLE_HEADERS}
                  data={rows}
                  selectedRows={[]}
                  totalValidRows={rows.length}
                  onSelectAll={() => {}}
                  stickyColumns={[0, 1, 2]}
                  renderCheckboxActions={() => {}}
                  checkboxHeaderRef={checkboxHeaderRef}
                  sortBy={sortBy}
                  ascending={ascending}
                  onSort={handleSort}
                  sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
                  sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
                >
                  {(row) => {
                    const rowId = row._id ?? row.id;
                    const isOpen = openMenu === rowId;

                    return (
                      <>
                        {/* checkbox — exact same as MyInvoicesTab */}
                        <td
                          className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                          style={{
                            width: 80,
                            minWidth: 80,
                            maxWidth: 80,
                            boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                          }}
                        >
                          <input
                            type="checkbox"
                            className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox checked:border-acs-checkbox after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                            readOnly
                          />
                        </td>

                        {/* action — exact same as MyInvoicesTab */}
                        <td
                          onClick={(e) => e.stopPropagation()}
                          className="sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                          style={{
                            width: 50,
                            minWidth: 50,
                            maxWidth: 50,
                            boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                          }}
                        >
                          <button
                            data-action-trigger
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                            onClick={(e) => handleActionClick(rowId, e)}
                          >
                            <MoreHorizontal size={18} className="mx-auto" />
                          </button>
                        </td>

                        {/* portal dropdown — exact same as MyInvoicesTab */}
                        {isOpen &&
                          ReactDOM.createPortal(
                            <div
                              ref={dropdownRef}
                              style={{
                                position: "absolute",
                                top: dropdownPos.top,
                                left: dropdownPos.left,
                                zIndex: 9999,
                              }}
                            >
                              <ActionMenu
                                rowId={rowId}
                                isOpen
                                onClose={() => setOpenMenu(null)}
                                moduleName="Transaction"
                                actions={[
                                  {
                                    icon: "Eye",
                                    label: "View",
                                    color: "blue",
                                    action: () => setOpenMenu(null),
                                  },
                                ]}
                              />
                            </div>,
                            document.body,
                          )}

                        {/* Transaction No. — sticky like Invoice Number */}
                        <td
                          className={`${TD_CLASS} sticky bg-white dark:bg-acs-dark-surface z-[9] font-mono text-[#223688] dark:text-[#6DE1C2] font-medium`}
                          style={{
                            ...TD_STYLE,
                            left: "130px",
                            boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                          }}
                        >
                          {row.transactionNumber}
                        </td>

                        {/* File Type */}
                        <td className={TD_CLASS} style={TD_STYLE}>
                          {formatFileType(row.fileType)}
                        </td>

                        {/* Flow Type */}
                        <td className={TD_CLASS} style={TD_STYLE}>
                          <span
                            className={`px-fluid-1.5 py-fluid-1 rounded font-medium ${
                              row.flowType === "INBOUND"
                                ? "bg-[#223688]/10 text-[#223688] dark:bg-[#223688]/30 dark:text-[#6DE1C2]"
                                : "bg-[#6DE1C2]/20 text-[#085041] dark:bg-[#6DE1C2]/20 dark:text-[#6DE1C2]"
                            }`}
                          >
                            {row.flowType === "INBOUND"
                              ? "Inbound"
                              : "Outbound"}
                          </span>
                        </td>

                        {/* Ref. Type */}
                        <td className={TD_CLASS} style={TD_STYLE}>
                          {row.referenceType && (
                            <span className="px-fluid-1.5 py-fluid-1 rounded font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              {row.referenceType}
                            </span>
                          )}
                        </td>

                        {/* Reference Number */}
                        <td className={TD_CLASS} style={TD_STYLE}>
                          {row.referenceNumber || "—"}
                        </td>

                        {/* File Name */}
                        <td
                          className={`${TD_CLASS} font-mono`}
                          style={TD_STYLE}
                        >
                          {row.fileName}
                        </td>

                        {/* Source */}
                        <td className={TD_CLASS} style={TD_STYLE}>
                          <span
                            className={`px-fluid-1.5 py-fluid-1 rounded font-medium ${
                              row.source === "IFS"
                                ? "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200" 
                                : row.source === "CARRIER_EDI"
                                  ? "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200"
                                  : row.source === "ASCEND"
                                    ? "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200"
                                    : "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200"
                            }`}
                          >
                            {row.source === "CARRIER_EDI"
                              ? "Carrier EDI"
                              : row.source || "—"}
                          </span>
                        </td>

                        {/* Processed — same badge style as Invoice */}
                        <td className={TD_CLASS} style={TD_STYLE}>
                          <span
                            className={`px-fluid-1.5 py-fluid-1 rounded font-medium ${
                              row.processingStatus === "SUCCESSFUL"
                                ? "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200"
                                : "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200"
                            }`}
                          >
                            {row.processingStatus === "SUCCESSFUL"
                              ? "Successful"
                              : "Unsuccessful"}
                          </span>
                        </td>

                        {/* Date/Time */}
                        <td className={TD_CLASS} style={TD_STYLE}>
                          {formatDateTime(row.dateTimeProcessed)}
                        </td>
                      </>
                    );
                  }}
                </Table>
              </div>
            </>
          )}
        </div>

        <GlobalPagination
          label="Transactions per page"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>
    );
};

export default TransactionAudit;